/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { FC, useState } from 'react';
import { Button } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import DoneIcon from '@mui/icons-material/Done';
import { getStatisticsReportContent } from './Statistics.actions';
import { showSnackbarError } from '../../shared/views/SnackbarNotification/store/SnackbarNotification.actions';
import { useDispatch } from 'react-redux';
import { PlatformAdminUIThunkType } from '../../store/store';
import { ShowSnackbar } from '../../shared/views/SnackbarNotification/store/SnackbarNotificationActionTypes';

interface StatisticsReportDownloadButtonProps {
	buttonText: string;
	fileName?: string;
}

const StatisticsReportDownloadButton: FC<StatisticsReportDownloadButtonProps> = ({
	buttonText,
	fileName
}: StatisticsReportDownloadButtonProps) => {
	const [isDownloaded, setIsDownloaded] = useState<boolean>(false);
	const dispatch = useDispatch<PlatformAdminUIThunkType<ShowSnackbar>>();

	const triggerFileDownload = (fileContent: string): void => {
		const contentType = 'text/' + (fileName?.substring(fileName.lastIndexOf('.') + 1) === 'csv' ? 'csv' : 'plain');
		const url = window.URL.createObjectURL(new Blob(['\ufeff', fileContent], { type: contentType }));
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName?.substring(fileName.lastIndexOf('/') + 1) || '';
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	return (
		<Button
			startIcon={isDownloaded ? <DoneIcon /> : <GetAppIcon />}
			style={{ marginRight: '1rem' }}
			disabled={fileName === undefined}
			onClick={(): void => {
				getStatisticsReportContent(fileName || '')
					.then(fileContent => {
						triggerFileDownload(fileContent);
						setIsDownloaded(true);
					})
					.catch(error => {
						console.log(error);
						dispatch(showSnackbarError(error));
					});
			}}
		>
			{buttonText}
		</Button>
	);
};

export default StatisticsReportDownloadButton;
